// Painel de Gestao do Catalogo Digital.
// Lista os produtos marcados para o catalogo publico e aponta, pra cada um,
// o que falta pra ficar "pronto pra publicar": foto, descricao comercial, vantagens.
// Permite incluir novos produtos (modal de busca + selecao em lote) e remover.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, AlertTriangle, Plus, Search, Trash2, RefreshCw, Camera, Wand2, BadgeCheck } from 'lucide-react';
import { api } from '../../lib/api';
import { useToast } from '../../components/Toast';
import { useDialog } from '../../components/DialogProvider';
import { MSG_PRODUTO_INATIVO, produtoInativo, SeloInativo } from '../../lib/produto-inativo';
import { Modal } from '../../components/Modal';
import { useStickyHeader } from '../../lib/useStickyHeader';
import { CatalogoAvisos } from './CatalogoAvisos';
import { CatalogoMetricas } from './CatalogoMetricas';
import { FotoProduto } from '../../components/FotoProduto';
import { type AvaliacaoFoto, type VereditoFoto, VEREDITO_FOTO, IconeVeredito } from './AvaliarFotoCatalogo';

type StatusGoogle = 'aprovado' | 'em_analise' | 'reprovado' | 'nao_encontrado';

interface ProdCatalogo {
  id: number;
  codigo_interno: string | null;
  descricao: string;
  marca_nome: string | null;
  grupo_nome: string | null;
  foto_url: string | null;
  descricao_comercial: string | null;
  vantagens: string | null;
  preco_pix: string;
  estoque_atual: string;
  status_google: StatusGoogle | null;
  motivo_google: string | null;
  verificado_google_em: string | null;
  master_completo: boolean;
}

interface RespostaCatalogoGestao {
  itens: ProdCatalogo[];
  google_ativo: boolean;
}

interface ResultadoSyncGoogle {
  aprovados: number;
  em_analise: number;
  reprovados: number;
  nao_encontrados: number;
  produtos_catalogo: number;
}

// Rotulo + cor de cada status. Cor + texto juntos — nao depende so da cor
// (seguro pra daltonismo).
const STATUS_GOOGLE: Record<StatusGoogle, { rotulo: string; cor: string }> = {
  aprovado: { rotulo: 'Aprovado', cor: '#16a34a' },
  em_analise: { rotulo: 'Em analise', cor: '#d97706' },
  reprovado: { rotulo: 'Reprovado', cor: '#dc2626' },
  nao_encontrado: { rotulo: 'Nao enviado', cor: '#9ca3af' },
};

interface ProdutoBusca {
  id: number;
  codigo_interno: string | null;
  descricao: string;
  marca_nome: string | null;
  foto_url: string | null;
  postar_catalogo: boolean;
  master_completo: boolean;
  status?: string | null;
}

const temTexto = (v: string | null) => !!v && v.trim().length > 0;

export function CatalogoGestao() {
  const navigate = useNavigate();
  const toast = useToast();
  const dialog = useDialog();
  const [itens, setItens] = useState<ProdCatalogo[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [soIncompletos, setSoIncompletos] = useState(false);
  const [busca, setBusca] = useState('');
  const [filtroGrupo, setFiltroGrupo] = useState('');
  const [aba, setAba] = useState<'produtos' | 'avisos' | 'acessos'>('produtos');
  const [qtdAvisos, setQtdAvisos] = useState(0);
  const [googleAtivo, setGoogleAtivo] = useState(false);
  const [sincronizando, setSincronizando] = useState(false);
  // Avaliacao de foto em lote — resultado por produto + progresso.
  const [fotosAval, setFotosAval] = useState<Map<number, AvaliacaoFoto>>(new Map());
  const [avaliandoFotos, setAvaliandoFotos] = useState(false);
  const [progressoFotos, setProgressoFotos] = useState<{ feito: number; total: number } | null>(null);
  const [otimizandoFoto, setOtimizandoFoto] = useState<number | null>(null);
  // Lote "Regerar do Master": refaz a derivada (1200x1200 branca) dos produtos
  // que tem Master OK mas estao com qualidade 'Refazer'.
  const [regerandoMaster, setRegerandoMaster] = useState(false);
  const [progressoMaster, setProgressoMaster] = useState<{ feito: number; total: number; ok: number; erro: number } | null>(null);

  // Modal de inclusao de produtos
  const [modalAberto, setModalAberto] = useState(false);
  const [buscaModal, setBuscaModal] = useState('');
  const [resultado, setResultado] = useState<ProdutoBusca[]>([]);
  const [buscando, setBuscando] = useState(false);
  const [selecionados, setSelecionados] = useState<Set<number>>(new Set());
  const [salvando, setSalvando] = useState(false);
  const buscaTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  async function carregar(): Promise<ProdCatalogo[]> {
    setCarregando(true);
    try {
      const r = await api.get<RespostaCatalogoGestao>('/api/produtos/catalogo-gestao');
      setItens(r.itens);
      setGoogleAtivo(r.google_ativo);
      return r.itens;
    } catch (e: any) {
      toast.erro(e.message);
      return [];
    } finally {
      setCarregando(false);
    }
  }

  // Le a Merchant API agora e atualiza o status de aprovacao de cada produto.
  async function sincronizarGoogle() {
    setSincronizando(true);
    try {
      const r = await api.post<ResultadoSyncGoogle>('/api/produtos/sincronizar-status-google', {});
      toast.sucesso(
        `Status atualizado: ${r.aprovados} aprovados, ${r.em_analise} em analise, ` +
          `${r.reprovados} reprovados, ${r.nao_encontrados} ainda nao enviados`,
      );
      await carregar();
    } catch (e: any) {
      toast.erro(e.message ?? 'Falha ao atualizar o status no Google');
    } finally {
      setSincronizando(false);
    }
  }

  // Contagem de avisos pendentes — alimenta o badge da aba.
  async function carregarContagemAvisos() {
    try {
      const lista = await api.get<unknown[]>('/api/produtos/catalogo-avisos');
      setQtdAvisos(lista.length);
    } catch {
      /* badge e secundario — falha silenciosa */
    }
  }

  useEffect(() => {
    // Avalia as fotos (parte tecnica, instantanea) assim que a lista carrega —
    // a coluna "Qualidade da foto" ja aparece preenchida, sem precisar do botao.
    carregar().then((lista) => avaliarFotos(lista, true));
    carregarContagemAvisos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const analisados = useMemo(
    () =>
      itens.map((p) => {
        const temFoto = !!p.foto_url;
        const temDesc = temTexto(p.descricao_comercial);
        const temVant = temTexto(p.vantagens);
        return { ...p, temFoto, temDesc, temVant, completo: temFoto && temDesc && temVant };
      }),
    [itens],
  );

  const completos = analisados.filter((p) => p.completo).length;
  const incompletos = analisados.length - completos;
  const buscaNorm = busca.trim().toLowerCase();
  const visiveis = analisados.filter((p) => {
    if (soIncompletos && p.completo) return false;
    if (filtroGrupo && (p.grupo_nome ?? '') !== filtroGrupo) return false;
    if (!buscaNorm) return true;
    return (
      (p.codigo_interno ?? '').toLowerCase().includes(buscaNorm) ||
      p.descricao.toLowerCase().includes(buscaNorm) ||
      (p.marca_nome ?? '').toLowerCase().includes(buscaNorm) ||
      (p.grupo_nome ?? '').toLowerCase().includes(buscaNorm)
    );
  });
  // Lista unica de grupos presentes no catalogo, ordenada — alimenta o combo.
  const gruposDisponiveis = Array.from(new Set(itens.map((p) => p.grupo_nome).filter((g): g is string => !!g))).sort((a, b) => a.localeCompare(b, 'pt-BR'));

  const sticky = useStickyHeader();

  // Check (forma redonda) vs Triangulo de alerta — formas distintas, seguras pra daltonismo.
  function statusIcone(ok: boolean) {
    return ok ? (
      <Check size={16} color="#16a34a" aria-label="ok" />
    ) : (
      <AlertTriangle size={16} color="#d97706" aria-label="faltando" />
    );
  }

  // Bolinha colorida + rotulo do status no Google. O texto acompanha a cor
  // (nao depende so dela — seguro pra daltonismo). Motivo no tooltip + abaixo.
  function statusGoogleCell(p: ProdCatalogo) {
    if (!p.status_google) {
      return <span style={{ color: 'var(--text-muted, #9ca3af)', fontSize: '0.8rem' }}>—</span>;
    }
    const info = STATUS_GOOGLE[p.status_google];
    return (
      <div title={p.motivo_google ?? info.rotulo}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              background: info.cor,
              flexShrink: 0,
            }}
          />
          <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>{info.rotulo}</span>
        </span>
        {p.motivo_google && (p.status_google === 'reprovado' || p.status_google === 'em_analise') && (
          <span
            style={{
              display: 'block',
              fontSize: '0.72rem',
              color: 'var(--text-muted, #6b7280)',
              marginTop: 2,
              lineHeight: 1.3,
            }}
          >
            {p.motivo_google}
          </span>
        )}
      </div>
    );
  }

  // Celula de qualidade da foto — preenchida apos clicar "Avaliar fotos".
  // Quando o produto tem foto Master completa, exibe o selo MASTER azul
  // (mesma identidade visual da tela Gestao de Fotos / aba Acervo).
  function qualidadeFotoCell(p: ProdCatalogo) {
    const av = fotosAval.get(p.id);
    const seloMaster = p.master_completo ? (
      <span
        title="Foto no padrao Master — alta resolucao, quadrada e fundo transparente"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 3,
          background: 'var(--accent)',
          color: 'var(--accent-fg)',
          fontSize: 10,
          fontWeight: 800,
          letterSpacing: 0.5,
          padding: '2px 6px',
          borderRadius: 999,
        }}
      >
        <BadgeCheck size={11} /> MASTER
      </span>
    ) : null;
    if (!av) {
      return seloMaster ?? <span style={{ color: 'var(--text-muted, #9ca3af)', fontSize: '0.8rem' }}>—</span>;
    }
    const info = VEREDITO_FOTO[av.veredito as VereditoFoto];
    const probs = [...(av.tecnico?.problemas ?? []), ...(av.visual?.problemas ?? [])];
    const podeOtimizar = !!av.tecnico?.otimizavel;
    // Se ja tem Master e a avaliacao tecnica e "boa", o selo MASTER substitui
    // o rotulo (e mais alto na hierarquia). Se for "refazer", mostramos os 2
    // pra alertar que a derivada saiu ruim mesmo com master OK.
    if (seloMaster && av.veredito === 'boa') {
      return <div title={info.rotulo}>{seloMaster}</div>;
    }
    return (
      <div title={probs.join(' · ') || info.rotulo}>
        {seloMaster && <div style={{ marginBottom: 3 }}>{seloMaster}</div>}
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <IconeVeredito veredito={av.veredito} size={15} />
          <span style={{ fontSize: '0.82rem', fontWeight: 600, color: info.cor }}>{info.rotulo}</span>
        </span>
        {av.visual?.parecer && (
          <span style={{ display: 'block', fontSize: '0.72rem', color: 'var(--text-muted, #6b7280)', marginTop: 2, lineHeight: 1.3 }}>
            {av.visual.parecer}
          </span>
        )}
        {podeOtimizar && (
          <button
            type="button"
            onClick={() => otimizarFotoLinha(p)}
            disabled={otimizandoFoto === p.id}
            style={{
              marginTop: 4,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              fontSize: '0.72rem',
              fontWeight: 600,
              color: '#b45309',
              background: 'transparent',
              border: '1px solid #d97706',
              borderRadius: 4,
              padding: '2px 6px',
              cursor: 'pointer',
            }}
          >
            <Wand2 size={12} /> {otimizandoFoto === p.id ? 'Otimizando...' : 'Otimizar'}
          </button>
        )}
      </div>
    );
  }

  // ---- Modal de inclusao ----
  function abrirModal() {
    setBuscaModal('');
    setResultado([]);
    setSelecionados(new Set());
    setModalAberto(true);
  }

  useEffect(() => {
    if (!modalAberto) return;
    if (buscaTimer.current) clearTimeout(buscaTimer.current);
    if (!buscaModal.trim()) {
      setResultado([]);
      return;
    }
    buscaTimer.current = setTimeout(async () => {
      try {
        setBuscando(true);
        const r = await api.get<ProdutoBusca[]>('/api/produtos', { query: { q: buscaModal, com_inativos: '1' } });
        // Esconde os que ja estao no catalogo
        setResultado(r.filter((p) => !p.postar_catalogo));
      } catch {
        setResultado([]);
      } finally {
        setBuscando(false);
      }
    }, 280);
    return () => {
      if (buscaTimer.current) clearTimeout(buscaTimer.current);
    };
  }, [buscaModal, modalAberto]);

  function toggleSel(id: number) {
    setSelecionados((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  }

  // "Marcar todos" ignora produtos sem master (bloqueados pela regra do catalogo) e os inativos.
  const elegiveis = resultado.filter((p) => p.master_completo && !produtoInativo(p));
  const todosMarcados = elegiveis.length > 0 && elegiveis.every((p) => selecionados.has(p.id));
  function marcarTodos() {
    setSelecionados((prev) => {
      const n = new Set(prev);
      if (todosMarcados) elegiveis.forEach((p) => n.delete(p.id));
      else elegiveis.forEach((p) => n.add(p.id));
      return n;
    });
  }

  async function confirmarInclusao() {
    if (selecionados.size === 0) return;
    try {
      setSalvando(true);
      await api.patch('/api/produtos/catalogo', { ids: [...selecionados], postar_catalogo: true });
      toast.sucesso(`${selecionados.size} produto(s) adicionado(s) ao catalogo`);
      setModalAberto(false);
      await carregar();
    } catch (e: any) {
      // Trava do servidor: 422 com lista dos sem master (cinto+suspensorio com a trava do UI).
      const lista = e?.corpo?.produtos_sem_master;
      if (Array.isArray(lista) && lista.length > 0) {
        const nomes = lista.slice(0, 5).map((p: any) => `${p.codigo_interno ?? '—'} ${p.descricao}`).join('\n');
        const extra = lista.length > 5 ? `\n…e mais ${lista.length - 5}` : '';
        await dialog.alerta(
          `Estes produtos nao tem foto Master:\n\n${nomes}${extra}\n\nGere o Master em Gestao de Fotos antes de adicionar ao catalogo.`,
          { titulo: 'Catalogo so aceita fotos Master', tipo: 'aviso' },
        );
      } else {
        toast.erro(e.message ?? 'Erro ao adicionar');
      }
    } finally {
      setSalvando(false);
    }
  }

  // Avalia a foto de cada produto do catalogo, em sequencia, com progresso.
  // Lote = so a parte tecnica (rapida). O parecer de IA fica na ficha do
  // produto, 1 de cada vez — ver aba Catalogo do cadastro.
  async function avaliarFotos(lista: ProdCatalogo[] = itens, silencioso = false) {
    if (lista.length === 0) return;
    setAvaliandoFotos(true);
    setProgressoFotos({ feito: 0, total: lista.length });
    try {
      for (let i = 0; i < lista.length; i++) {
        const p = lista[i]!;
        try {
          const r = await api.post<AvaliacaoFoto>(`/api/produtos/${p.id}/avaliar-foto`, { comIA: false });
          setFotosAval((prev) => new Map(prev).set(p.id, r));
        } catch {
          /* falha numa foto nao derruba o lote */
        }
        setProgressoFotos({ feito: i + 1, total: lista.length });
      }
      if (!silencioso) toast.sucesso('Avaliacao das fotos concluida');
    } finally {
      setAvaliandoFotos(false);
      setProgressoFotos(null);
    }
  }

  // Otimiza a foto de um produto (pad quadrado 1200x1200 + WebP).
  async function otimizarFotoLinha(p: ProdCatalogo) {
    setOtimizandoFoto(p.id);
    try {
      await api.post(`/api/produtos/${p.id}/otimizar-foto`, {});
      toast.sucesso('Foto otimizada');
      setFotosAval((prev) => {
        const n = new Map(prev);
        n.delete(p.id); // resultado antigo invalidado
        return n;
      });
      await carregar();
    } catch (e: any) {
      toast.erro(e.message ?? 'Falha ao otimizar a foto');
    } finally {
      setOtimizandoFoto(null);
    }
  }

  // Regera em lote a derivada dos produtos do catalogo que tem Master completo
  // mas estao com qualidade "Refazer". Cada regeracao usa o Master alta-res como
  // fonte e gera derivada 1200x1200 branca (foto exibida no catalogo publico).
  async function regerarDerivadasDoMaster() {
    const alvos = itens.filter((p) => {
      if (!p.master_completo) return false;
      const av = fotosAval.get(p.id);
      return av?.veredito === 'refazer';
    });
    if (alvos.length === 0) {
      toast.sucesso('Nenhum produto pra regerar (todos com Master estao OK).');
      return;
    }
    const ok = await dialog.confirmar(
      `Regerar a foto de ${alvos.length} produto(s) a partir do Master?\n\nIsso refaz a derivada 1200x1200 branca usada no catalogo publico.`,
      { titulo: 'Regerar do Master' },
    );
    if (!ok) return;
    setRegerandoMaster(true);
    setProgressoMaster({ feito: 0, total: alvos.length, ok: 0, erro: 0 });
    let acOk = 0, acErro = 0;
    for (let i = 0; i < alvos.length; i++) {
      const p = alvos[i]!;
      try {
        await api.post(`/api/produtos/${p.id}/regerar-derivada-do-master`, {});
        acOk++;
      } catch {
        acErro++;
      }
      setProgressoMaster({ feito: i + 1, total: alvos.length, ok: acOk, erro: acErro });
    }
    setRegerandoMaster(false);
    setProgressoMaster(null);
    toast.sucesso(`Regeradas: ${acOk}${acErro > 0 ? ` · falhas: ${acErro}` : ''}. Reavaliando qualidade...`);
    // Recarrega lista (foto_url mudou) + re-avalia qualidade tecnica em lote.
    const lista = await carregar();
    setFotosAval(new Map()); // invalida avaliacoes antigas
    void avaliarFotos(lista, true);
  }

  async function removerDoCatalogo(p: ProdCatalogo) {
    const ok = await dialog.confirmar(
      `Remover "${p.descricao}" do catalogo digital? O produto continua cadastrado normalmente — so deixa de aparecer na vitrine publica.`,
      { perigoso: true, imagem: p.foto_url ?? undefined },
    );
    if (!ok) return;
    try {
      await api.patch('/api/produtos/catalogo', { ids: [p.id], postar_catalogo: false });
      toast.sucesso('Produto removido do catalogo');
      await carregar();
    } catch (e: any) {
      toast.erro(e.message ?? 'Erro ao remover');
    }
  }

  return (
    <div>
      {/* Abas: produtos do catalogo x avisos de clientes */}
      <div style={{ display: 'flex', gap: 2, borderBottom: '2px solid var(--panel-border, #e5e7eb)', marginBottom: 14 }}>
        {(['produtos', 'avisos', 'acessos'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setAba(t)}
            style={{
              padding: '8px 16px',
              border: 'none',
              borderBottom: aba === t ? '2px solid var(--tabs-active-fg, var(--primary))' : '2px solid transparent',
              marginBottom: -2,
              background: 'transparent',
              color: aba === t ? 'var(--text-color)' : 'var(--text-muted, #6b7280)',
              fontWeight: aba === t ? 700 : 500,
              cursor: 'pointer',
              fontSize: 14,
            }}
          >
            {t === 'produtos'
              ? 'Produtos do catalogo'
              : t === 'acessos'
                ? 'Acessos'
                : `Avisos de clientes${qtdAvisos > 0 ? ` (${qtdAvisos})` : ''}`}
          </button>
        ))}
      </div>

      {aba === 'avisos' ? (
        <CatalogoAvisos onAtualizou={carregarContagemAvisos} />
      ) : aba === 'acessos' ? (
        <CatalogoMetricas />
      ) : (
        <>
      <div ref={sticky.headerRef} style={sticky.headerStyle}>
        <div className="pagina-header">
          <h2 className="pagina-titulo">
            Catalogo Digital{' '}
            <span className="muted" style={{ fontSize: 14, fontWeight: 400 }}>
              ({visiveis.length === itens.length ? itens.length : `${visiveis.length} de ${itens.length}`})
            </span>
          </h2>
          <div style={{ position: 'relative', flex: 1, maxWidth: 360, marginLeft: 12 }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted, #9ca3af)', pointerEvents: 'none' }} />
            <input
              type="search"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Pesquisar codigo, descricao, marca ou grupo..."
              style={{ width: '100%', padding: '6px 10px 6px 30px', border: '1px solid var(--input-border, #e5e7eb)', borderRadius: 6, fontSize: 13, background: 'var(--input-bg, #fff)' }}
            />
          </div>
          <div className="pagina-acoes">
            {googleAtivo && (
              <button
                type="button"
                className="btn-secundario"
                onClick={sincronizarGoogle}
                disabled={sincronizando}
                title="Consultar o Google Merchant Center agora e atualizar o status de cada produto"
              >
                <RefreshCw size={14} style={{ display: 'inline', marginRight: 4 }} />
                {sincronizando ? 'Atualizando...' : 'Atualizar status do Google'}
              </button>
            )}
            <button
              type="button"
              className="btn-secundario"
              onClick={() => avaliarFotos()}
              disabled={avaliandoFotos || itens.length === 0}
              title="Reavalia a qualidade tecnica da foto de cada produto (resolucao, formato, proporcao)"
            >
              <Camera size={14} style={{ display: 'inline', marginRight: 4 }} />
              {avaliandoFotos
                ? `Avaliando ${progressoFotos?.feito ?? 0}/${progressoFotos?.total ?? 0}...`
                : 'Reavaliar fotos'}
            </button>
            {(() => {
              const refazerComMaster = itens.filter((p) => p.master_completo && fotosAval.get(p.id)?.veredito === 'refazer').length;
              return (
                <button
                  type="button"
                  className="btn-secundario"
                  onClick={regerarDerivadasDoMaster}
                  disabled={regerandoMaster || refazerComMaster === 0}
                  title={refazerComMaster === 0
                    ? 'Sem produtos pra regerar (todos com Master estao OK).'
                    : `Refaz a foto exibida (1200x1200 branca) a partir do Master pra ${refazerComMaster} produto(s) com 'Refazer'.`}
                >
                  <Wand2 size={14} style={{ display: 'inline', marginRight: 4 }} />
                  {regerandoMaster
                    ? `Regerando ${progressoMaster?.feito ?? 0}/${progressoMaster?.total ?? 0}...`
                    : `Regerar do Master${refazerComMaster > 0 ? ` (${refazerComMaster})` : ''}`}
                </button>
              );
            })()}
            <button type="button" className="btn-primario" onClick={abrirModal}>
              <Plus size={14} style={{ display: 'inline', marginRight: 4 }} /> Adicionar produtos
            </button>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'center', fontSize: 13 }}>
          <span>
            <strong style={{ color: '#16a34a' }}>{completos}</strong> prontos pra publicar
          </span>
          <span>
            <strong style={{ color: '#d97706' }}>{incompletos}</strong> incompletos
          </span>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={soIncompletos}
              onChange={(e) => setSoIncompletos(e.target.checked)}
            />
            Mostrar só incompletos
          </label>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <span style={{ fontWeight: 600 }}>Grupo:</span>
            <select
              value={filtroGrupo}
              onChange={(e) => setFiltroGrupo(e.target.value)}
              style={{ padding: '4px 8px', border: '1px solid var(--input-border, #e5e7eb)', borderRadius: 4, fontSize: 13, background: 'var(--input-bg, #fff)', minWidth: 260 }}
            >
              <option value="">TODOS</option>
              {gruposDisponiveis.map((g) => (
                <option key={g} value={g}>{g}</option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {carregando ? (
        <p className="muted">Carregando...</p>
      ) : itens.length === 0 ? (
        <div className="muted" style={{ padding: 24, lineHeight: 1.7 }}>
          Nenhum produto no catalogo ainda.
          <br />
          Clique em <strong>"Adicionar produtos"</strong> acima para incluir os primeiros.
        </div>
      ) : visiveis.length === 0 ? (
        <p className="muted" style={{ padding: 16 }}>
          Todos os {itens.length} produtos do catalogo estao completos.
        </p>
      ) : (
        <table className="tabela" style={sticky.tableStyle}>
          <thead>
            <tr>
              <th style={{ ...sticky.thStyle, width: 70 }}>FOTO</th>
              <th style={{ ...sticky.thStyle, width: 80 }}>COD.</th>
              <th style={sticky.thStyle}>DESCRICAO</th>
              <th style={sticky.thStyle}>MARCA</th>
              <th style={sticky.thStyle}>GRUPO</th>
              <th style={{ ...sticky.thStyle, textAlign: 'center', width: 70 }} title="Tem foto cadastrada?">
                FOTO
              </th>
              <th
                style={{ ...sticky.thStyle, textAlign: 'center', width: 140 }}
                title="Tem descricao comercial preenchida?"
              >
                DESCR. COMERCIAL
              </th>
              <th style={{ ...sticky.thStyle, textAlign: 'center', width: 95 }} title="Tem vantagens preenchidas?">
                VANTAGENS
              </th>
              <th style={{ ...sticky.thStyle, width: 200 }}>SITUACAO</th>
              {googleAtivo && (
                <th style={{ ...sticky.thStyle, width: 190 }} title="Status de aprovacao no Google Merchant Center">
                  STATUS NO GOOGLE
                </th>
              )}
              <th style={{ ...sticky.thStyle, width: 210 }} title="Qualidade da foto pro catalogo — clique em 'Avaliar fotos'">
                QUALIDADE DA FOTO
              </th>
              <th style={{ ...sticky.thStyle, width: 50, textAlign: 'center' }}></th>
            </tr>
          </thead>
          <tbody>
            {visiveis.map((p) => (
              <tr
                key={p.id}
                onClick={() => navigate(`/produtos/${p.id}?aba=catalogo`)}
                style={{ cursor: 'pointer' }}
                title="Abrir cadastro na aba Catalogo"
              >
                <td>
                  {p.foto_url ? (
                    <img src={p.foto_url} alt="" className="produto-thumb" loading="lazy" />
                  ) : (
                    <div className="produto-thumb-vazio" />
                  )}
                </td>
                <td>
                  <span className="codigo-curto">{p.codigo_interno ?? '—'}</span>
                </td>
                <td>{p.descricao}</td>
                <td>{p.marca_nome ?? '—'}</td>
                <td>{p.grupo_nome ?? '—'}</td>
                <td style={{ textAlign: 'center' }}>{statusIcone(p.temFoto)}</td>
                <td style={{ textAlign: 'center' }}>{statusIcone(p.temDesc)}</td>
                <td style={{ textAlign: 'center' }}>{statusIcone(p.temVant)}</td>
                <td>
                  {p.completo ? (
                    <span
                      style={{
                        backgroundColor: '#fde68a',
                        color: '#000',
                        padding: '2px 8px',
                        borderRadius: 4,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                      }}
                    >
                      PRONTO
                    </span>
                  ) : (
                    <span style={{ color: '#d97706', fontSize: '0.78rem', fontWeight: 500 }}>
                      Falta:{' '}
                      {[
                        !p.temFoto && 'foto',
                        !p.temDesc && 'descricao comercial',
                        !p.temVant && 'vantagens',
                      ]
                        .filter(Boolean)
                        .join(', ')}
                    </span>
                  )}
                </td>
                {googleAtivo && <td>{statusGoogleCell(p)}</td>}
                <td onClick={(e) => e.stopPropagation()}>{qualidadeFotoCell(p)}</td>
                <td style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    onClick={() => removerDoCatalogo(p)}
                    title="Remover do catalogo"
                    style={{ background: 'transparent', border: 'none', color: '#dc2626', cursor: 'pointer', padding: 4 }}
                  >
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Modal: adicionar produtos ao catalogo */}
      <Modal
        aberto={modalAberto}
        onFechar={() => setModalAberto(false)}
        titulo="Adicionar produtos ao catalogo"
        largura={620}
        rodape={
          <>
            <button type="button" className="btn-secundario" onClick={() => setModalAberto(false)} disabled={salvando}>
              Cancelar
            </button>
            <button
              type="button"
              className="btn-primario"
              onClick={confirmarInclusao}
              disabled={salvando || selecionados.size === 0}
            >
              {salvando ? 'Adicionando...' : `Adicionar ${selecionados.size || ''} ao catalogo`}
            </button>
          </>
        }
      >
        <div style={{ position: 'relative', marginBottom: 10 }}>
          <Search size={16} style={{ position: 'absolute', left: 10, top: 10, color: 'var(--text-muted, #6b7280)' }} />
          <input
            type="text"
            autoFocus
            placeholder="Buscar produto por codigo, descricao ou EAN..."
            value={buscaModal}
            onChange={(e) => setBuscaModal(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 10px 8px 34px',
              border: '1px solid var(--input-border, #d1d5db)',
              borderRadius: 6,
              background: 'var(--input-bg)',
              color: 'var(--text-color)',
              fontSize: 14,
            }}
          />
        </div>

        {buscando && <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-muted, #6b7280)' }}>Buscando...</div>}
        {!buscando && buscaModal.trim() && resultado.length === 0 && (
          <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-muted, #6b7280)' }}>
            Nenhum produto fora do catalogo encontrado.
          </div>
        )}
        {!buscaModal.trim() && (
          <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-muted, #6b7280)', fontSize: 13 }}>
            Digite acima para buscar produtos. Quem ja esta no catalogo nao aparece.
          </div>
        )}

        {resultado.length > 0 && (
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '8px 6px',
              borderBottom: '2px solid var(--panel-border, #e5e7eb)',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: 13,
            }}
          >
            <input
              type="checkbox"
              checked={todosMarcados}
              onChange={marcarTodos}
              style={{ width: 16, height: 16, flexShrink: 0 }}
            />
            Marcar todos ({resultado.length})
          </label>
        )}

        {resultado.map((p) => {
          const marcado = selecionados.has(p.id);
          // Bloqueia selecao se nao tem master completo (regra desde 2026-05-20):
          // catalogo so aceita produtos com foto Master.
          const bloqueado = !p.master_completo;
          return (
            <label
              key={p.id}
              title={bloqueado ? 'Sem foto Master. Gere o Master em Gestao de Fotos antes de adicionar ao catalogo.' : undefined}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '6px 6px',
                borderBottom: '1px solid var(--grid-row-border, #f3f4f6)',
                cursor: bloqueado ? 'not-allowed' : 'pointer',
                background: marcado ? 'var(--menu-ativo-bg, #dbeafe)' : 'transparent',
                opacity: bloqueado ? 0.55 : 1,
              }}
            >
              <input
                type="checkbox" checked={marcado} disabled={bloqueado}
                onChange={() => {
                  if (produtoInativo(p)) { void dialog.alerta(MSG_PRODUTO_INATIVO); return; }
                  if (!bloqueado) toggleSel(p.id);
                }}
                style={{ width: 16, height: 16, flexShrink: 0 }}
              />
              {p.foto_url ? (
                <FotoProduto url={p.foto_url} tam={36} style={{ flexShrink: 0 }} />
              ) : (
                <div style={{ width: 36, height: 36, borderRadius: 4, background: 'var(--grid-header-bg)', border: '1px solid var(--panel-border, #e5e7eb)' }} />
              )}
              <span style={{ fontWeight: 600, color: 'var(--text-muted, #6b7280)', minWidth: 44 }}>{p.codigo_interno ?? '—'}</span>
              <span style={{ flex: 1, minWidth: 0 }}>{produtoInativo(p) && <SeloInativo />}{p.descricao}</span>
              {bloqueado && (
                <span style={{ fontSize: 10, fontWeight: 700, color: '#b45309', background: '#fef3c7', padding: '2px 6px', borderRadius: 4, whiteSpace: 'nowrap' }}>
                  SEM MASTER
                </span>
              )}
              <span style={{ fontSize: 12, color: 'var(--text-muted, #6b7280)' }}>{p.marca_nome ?? ''}</span>
            </label>
          );
        })}
      </Modal>
        </>
      )}
    </div>
  );
}
